import { cn } from "@/lib/utils";
import type { FlowItem, Scope } from "./types";

export const ScopePill = ({ scope }: { scope: Scope }) => {
  const isBrand = scope === "brand";
  return (
    <span
      className={cn(
        "inline-flex items-center text-[11px] font-semibold uppercase tracking-wide px-2 py-0.5 rounded-full border",
        isBrand
          ? "bg-primary-tint-soft text-primary border-primary/20"
          : "bg-status-success-soft text-foreground border-trend-positive/20"
      )}
    >
      {isBrand ? "Brand-scoped" : "Portfolio-scoped"}
    </span>
  );
};

export const SectionTitle = ({ id, children, scope }: { id: string; children: React.ReactNode; scope?: Scope }) => (
  <div id={id} className="mt-10 mb-4 pb-2 border-b border-border flex items-center gap-3 flex-wrap scroll-mt-20">
    <h2 className="text-2xl font-semibold text-foreground">{children}</h2>
    {scope && <ScopePill scope={scope} />}
  </div>
);

export const Sub = ({ children, id }: { children: React.ReactNode; id?: string }) => (
  <h3 id={id} className="text-lg font-semibold text-foreground mt-6 mb-2 scroll-mt-6">{children}</h3>
);

export const RefLink = ({ to, children }: { to: string; children: React.ReactNode }) => (
  <button
    onClick={() => document.getElementById(to)?.scrollIntoView({ behavior: "smooth" })}
    className="text-primary font-semibold hover:underline transition-colors"
  >
    {children}
  </button>
);

export const P = ({ children, id }: { children: React.ReactNode; id?: string }) => (
  <p id={id} className="text-sm text-muted-foreground leading-relaxed mb-3 scroll-mt-6">{children}</p>
);

export const Pill = ({ children, variant = "default" }: { children: React.ReactNode; variant?: string }) => {
  const colors: Record<string, string> = {
    default: "bg-primary-tint-soft text-primary",
    green: "bg-status-success-soft text-foreground",
    orange: "bg-orange-500/10 text-orange-600",
    muted: "bg-muted text-muted-foreground",
  };
  return (
    <span className={`inline-block text-xs font-medium px-2 py-0.5 rounded ${colors[variant] || colors.default}`}>
      {children}
    </span>
  );
};

export const ConnectsTo = ({ pages }: { pages: string[] }) => (
  <div className="flex items-center gap-1.5 flex-wrap mt-1">
    <span className="text-xs text-muted-foreground">Connects to:</span>
    {pages.map((p) => (
      <Pill key={p}>{p}</Pill>
    ))}
  </div>
);

export const ApiBlock = ({ children }: { children: React.ReactNode }) => (
  <div className="rounded-sm border border-border bg-surface-tint-soft p-4 text-xs text-muted-foreground font-mono leading-relaxed mb-4 overflow-x-auto">
    {children}
  </div>
);

export const FlowSteps = ({ items, showLegend = true }: { items: FlowItem[]; showLegend?: boolean }) => (
  <div className="rounded-sm border border-border bg-surface-tint-subtle p-5 space-y-4 mb-4">
    <div className="flex items-center justify-between gap-4">
      <h5 className="text-base font-semibold leading-6 text-muted-foreground">Process</h5>
      {showLegend && (
        <div className="flex items-center gap-4 text-xs text-muted-foreground">
          <span className="inline-flex items-center gap-1.5">
            <span className="w-2.5 h-2.5 rounded-full bg-primary" />
            You
          </span>
          <span className="inline-flex items-center gap-1.5">
            <span className="w-2.5 h-2.5 rounded-full bg-gradient-to-br from-[#342799] via-[#A242A2] to-[#FF534C]" />
            Signal (automated)
          </span>
        </div>
      )}
    </div>
    <ol className="relative space-y-5">
      <div
        aria-hidden
        className="absolute left-3 top-3 bottom-3 w-px bg-border"
      />
      {items.map((it, i) => (
        <li key={i} className="relative flex items-start gap-3">
          <span
            className={cn(
              "relative z-10 flex items-center justify-center w-6 h-6 rounded-full text-[11px] font-semibold flex-shrink-0",
              it.actor === "user"
                ? "bg-primary text-primary-foreground"
                : "bg-gradient-to-br from-[#342799] via-[#A242A2] to-[#FF534C] text-white",
            )}
          >
            {i + 1}
          </span>
          <div className="flex-1 min-w-0 pt-0.5 flex items-center gap-2 flex-wrap">
            <span className="text-sm text-foreground">{it.label}</span>
            <span
              className={cn(
                "text-[11px] uppercase tracking-wide",
                it.actor === "user"
                  ? "text-muted-foreground"
                  : "bg-gradient-to-r from-[#342799] via-[#A242A2] to-[#FF534C] bg-clip-text text-transparent font-semibold",
              )}
            >
              {it.actor === "user" ? "You" : "Signal"}
            </span>
          </div>
        </li>
      ))}
    </ol>
  </div>
);
